import * as React from 'react';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import {CoreEntityConfirmationDialog} from 'shells/core-entity-confirmation-dialog/core-entity-confirmation-dialog';
import {EditableTextfieldContent} from 'shells/editable-textfield-content/editable-textfield-content';
import {NotePencilRegularIcon} from 'shells/icon/svg/note-pencil-regular-icon';
import {NoteRegularIcon} from 'shells/icon/svg/note-regular-icon';
import {TrashRegularIcon} from 'shells/icon/svg/trash-regular-icon';
import {BaseRow} from 'shells/row/base-row/base-row';

import type {WidgetFragment as Widget, CompetitorForLeadFragment} from 'nutshell-graphql-types';

import {CardWidgetWrapper} from '../../../core/card/card-widget-wrapper';
import {CollapsibleCardWidget} from '../../../core/card/collapsible-card-widget';
import {getWidgetEmptyStateProps} from '../../../core/card/helpers';

import {refetchForEvent} from '../../refetch-helpers';

import {CompetitorPopover} from './components/competitor-popover';
import {CompetitorsRowCreateForm} from './components/competitors-row-create-form';
import {
    useGetCompetitorsForLead,
    useAddCompetitorToLead,
    useRemoveCompetitorFromLead,
    useLeadUpdateCompetitor,
} from './graphql/hooks';

type Props = {
    leadId: string;
    widget?: Widget;
};

export function CompetitorsWidgetWrapper(props: Props) {
    const [isAddingCompetitor, setIsAddingCompetitor] = React.useState<boolean>(false);
    const [competitorNoteToEdit, setCompetitorNoteToEdit] = React.useState<
        CompetitorForLeadFragment | null | undefined
    >(null);
    const [error, setError] = React.useState<string | null | undefined>(null);
    const [isPopoverOpen, setIsPopoverOpen] = React.useState<boolean>(false);
    const [selectedCompetitor, setSelectedCompetitor] = React.useState<
        CompetitorForLeadFragment | null | undefined
    >(null);
    const [removeConfirmationForCompetitorId, setRemoveConfirmationForCompetitorId] =
        React.useState<string | null | undefined>(null);

    const removeButtonRef = React.useRef<HTMLElement | null | undefined>(null);
    const popoverRef = React.useRef<HTMLElement | null | undefined>(null);
    const rowRefs = React.useRef<{
        [id: string]: {
            current: HTMLElement | null | undefined;
        };
    }>({});

    const {competitors, isLoading} = useGetCompetitorsForLead(props.leadId);
    const {addCompetitorToLead} = useAddCompetitorToLead();
    const {removeCompetitorFromLead} = useRemoveCompetitorFromLead();
    const {leadUpdateCompetitor} = useLeadUpdateCompetitor();

    const handleEventTrigger = React.useCallback(() => {
        if (NutClientConfig.hasLeadPageRedesignM3FF) {
            refetchForEvent('competitorsChanged');
        } else {
            $(window).trigger(Lead.events.LeadBackboneModelDidUpdate);
        }
    }, []);

    const handleSelect = React.useCallback(
        (competitor: {value: string}) => {
            addCompetitorToLead({
                lead: props.leadId,
                competitor: competitor.value,
            })
                .then(() => {
                    handleEventTrigger();
                })
                .catch((e) => {
                    setError(safelyGetGraphQLErrorMessage(e));
                })
                .finally(() => {
                    setIsAddingCompetitor(false);
                });
        },
        [addCompetitorToLead, props.leadId, handleEventTrigger]
    );

    const handleCreate = React.useCallback(
        (competitorName: string) => {
            addCompetitorToLead({
                lead: props.leadId,
                name: competitorName,
            })
                .then(() => {
                    handleEventTrigger();
                })
                .catch((e) => {
                    setError(safelyGetGraphQLErrorMessage(e));
                })
                .finally(() => {
                    setIsAddingCompetitor(false);
                });
        },
        [addCompetitorToLead, props.leadId, handleEventTrigger]
    );

    const handleRemoveCompetitor = React.useCallback(
        (competitorId: string) => {
            removeCompetitorFromLead({
                lead: props.leadId,
                competitor: competitorId,
            })
                .then(() => {
                    handleEventTrigger();
                })
                .catch((e) => {
                    setError(safelyGetGraphQLErrorMessage(e));
                });
        },
        [removeCompetitorFromLead, props.leadId, handleEventTrigger]
    );

    const handleUpdateNote = React.useCallback(
        (competitorId: string, note?: string | null) => {
            leadUpdateCompetitor({
                lead: props.leadId,
                competitor: competitorId,
                note,
            })
                .then(() => {
                    setCompetitorNoteToEdit(null);
                })
                .catch((e) => {
                    setError(safelyGetGraphQLErrorMessage(e));
                });
        },
        [leadUpdateCompetitor, props.leadId]
    );

    const getCompetitorRowRef = React.useCallback((competitorId: string) => {
        if (!rowRefs.current[competitorId]) {
            rowRefs.current[competitorId] = {current: null};
        }

        return rowRefs.current[competitorId];
    }, []);

    const count = competitors ? competitors.totalCount : 0;
    const isEmpty = count === 0 && !isAddingCompetitor;

    const content = (
        <>
            {isAddingCompetitor && (
                <CompetitorsRowCreateForm
                    onSelect={handleSelect}
                    onCreate={handleCreate}
                    onCancel={() => setIsAddingCompetitor(false)}
                />
            )}
            {competitors &&
                competitors.edges.map((competitor) => {
                    const toolbarOptions = [
                        {
                            id: 'competitor-note',
                            tooltipText: competitor.relationshipNote ? 'Edit note' : 'Add note',
                            icon: competitor.relationshipNote
                                ? NotePencilRegularIcon
                                : NoteRegularIcon,
                            onClick: () => {
                                setCompetitorNoteToEdit(competitor.node);
                            },
                        },
                        {
                            id: 'competitor-remove',
                            tooltipText: 'Remove competitor',
                            icon: TrashRegularIcon,
                            onClick: () => {
                                setRemoveConfirmationForCompetitorId(competitor.node.id);
                            },
                            // @ts-expect-error
                            getRef: (ref) => {
                                removeButtonRef.current = ref;
                            },
                        },
                    ];

                    const isEditingNote = Boolean(
                        competitorNoteToEdit && competitorNoteToEdit.id === competitor.node.id
                    );

                    return (
                        <React.Fragment key={competitor.node.id}>
                            <BaseRow
                                title={competitor.node.name || 'Unnamed competitor'}
                                toolbarProps={{
                                    options: toolbarOptions,
                                    forceHideToolbar: isEditingNote,
                                    forceShowToolbar:
                                        removeConfirmationForCompetitorId === competitor.node.id,
                                }}
                                type='competitors'
                                getRowRef={getCompetitorRowRef(competitor.node.id)}
                                onClick={() => {
                                    const rowRef = rowRefs.current[competitor.node.id];
                                    popoverRef.current = rowRef && rowRef.current;
                                    setSelectedCompetitor(competitor.node);
                                    setIsPopoverOpen(true);
                                }}
                            >
                                <>
                                    {(competitor.relationshipNote || isEditingNote) && (
                                        <EditableTextfieldContent
                                            value={competitor.relationshipNote}
                                            onUpdate={(note) =>
                                                handleUpdateNote(competitor.node.id, note)
                                            }
                                            isMultiline={true}
                                            isEditing={isEditingNote}
                                            setIsEditing={(isEditing) => {
                                                if (isEditing) {
                                                    setCompetitorNoteToEdit(competitor.node);
                                                } else {
                                                    setCompetitorNoteToEdit(null);
                                                }
                                            }}
                                        />
                                    )}
                                </>
                            </BaseRow>
                            {removeConfirmationForCompetitorId === competitor.node.id && (
                                <CoreEntityConfirmationDialog
                                    anchor={removeButtonRef.current}
                                    type='competitor'
                                    parentEntity='lead'
                                    onConfirm={() => {
                                        setRemoveConfirmationForCompetitorId(null);
                                        handleRemoveCompetitor(competitor.node.id);
                                    }}
                                    onCancel={() => {
                                        setRemoveConfirmationForCompetitorId(null);
                                    }}
                                />
                            )}
                        </React.Fragment>
                    );
                })}
        </>
    );

    return (
        <>
            {NutClientConfig.hasLeadPageRedesignM3FF ? (
                props.widget && (
                    <CollapsibleCardWidget
                        widget={props.widget}
                        addButtonProps={{
                            variant: 'singular',
                            onClick: () => setIsAddingCompetitor(true),
                            isDisabled: isAddingCompetitor,
                        }}
                        errorProps={{error, setError}}
                        emptyStateProps={getWidgetEmptyStateProps('leadCompetitors', isEmpty)}
                        isLoading={isLoading}
                        count={count}
                        setIsAdding={(isAdding) => {
                            setIsAddingCompetitor(isAdding);
                        }}
                    >
                        {content}
                    </CollapsibleCardWidget>
                )
            ) : (
                <CardWidgetWrapper
                    widgetKey='leadCompetitors'
                    addButtonProps={{
                        variant: 'singular',
                        onClick: () => setIsAddingCompetitor(true),
                        isDisabled: isAddingCompetitor,
                    }}
                    errorProps={{error, setError}}
                    count={count}
                    isLoading={isLoading}
                    isEmpty={isEmpty}
                >
                    {content}
                </CardWidgetWrapper>
            )}
            {isPopoverOpen && selectedCompetitor && (
                <CompetitorPopover
                    anchor={popoverRef.current}
                    competitor={selectedCompetitor}
                    onClose={() => setIsPopoverOpen(false)}
                />
            )}
        </>
    );
}
